import { useCallback, useContext } from "react";
import { useSelector } from "react-redux";
import { useMutation } from "@tanstack/react-query";
import { JoinRoomStatus, updateRoomStatus } from "store/rooms";
import { useAppDispatch } from "store/hooks";
import { addErrorNotification } from "modules/notification/redux/notificationSlice";
import { selectCurrentEventId } from "modules/event/selectors";
import { Actions, trackActionEnd, trackActionStart } from "modules/monitoring";

import { useI18n } from "i18n";
import { addNotificationForHostSeat } from "modules/joinRoom/host.seat.firebase";
import { selectUserId } from "modules/auth/redux/selectors";
import {
  getErrorMessage,
  shouldRequestHostSeat,
} from "modules/joinTable/errorHandler";
import { resetUserForceMove } from "modules/userForceMove/redux/slice";
import { deleteEventUserNotification } from "services/firebaseService/eventUserNotifications";
import {
  UserForceMovePayload,
  userForceMove,
} from "modules/userForceMove/request";
import { useClearFloorDataAndRegisterListeners } from "modules/joinFloor/hooks/useClearFloorDataAndRegisterListeners";
import logger from "logging/logger";
import { useUserData } from "modules/audioVideo/hooks";
import { Events, TRACKING_CONTEXT } from "modules/tracking";

export const useUserForceMove = () => {
  const eventId = useSelector(selectCurrentEventId);
  const userId = useSelector(selectUserId);
  const { clearFloorDataAndRegisterListeners } =
    useClearFloorDataAndRegisterListeners();
  const { track } = useContext(TRACKING_CONTEXT);
  const { setConversationId } = useUserData();

  const { t } = useI18n(["server", "template"]);

  const dispatch = useAppDispatch();

  const { mutate, isPending, status } = useMutation({
    mutationKey: ["remo.userForceMove"],

    mutationFn: async (payload: UserForceMovePayload) => {
      trackActionStart(Actions.JOIN_ROOM);

      if (payload.currentFloorId !== payload.targetFloorId) {
        trackActionStart(Actions.JOIN_FLOOR);
      }

      dispatch(updateRoomStatus(JoinRoomStatus.IN_PROGRESS));

      return userForceMove(payload);
    },
    onSuccess: async (
      _,
      { targetTableId, targetFloorId, currentFloorId, userId },
    ) => {
      dispatch(updateRoomStatus(JoinRoomStatus.DONE));

      track(Events.MOVE_USER_SUCCESS, {
        eventId,
        userId,
      });
      trackActionEnd(Actions.JOIN_ROOM, Actions.JOIN_ROOM_SUCCESS, {
        targetTableId,
        eventId,
        floorId: targetFloorId,
      });

      setConversationId({ conversationId: targetTableId });

      if (currentFloorId !== targetFloorId) {
        logger.info("user switched floors", {
          targetFloorId,
          eventId,
          currentFloorId,
        });
        clearFloorDataAndRegisterListeners(targetFloorId);
        trackActionEnd(Actions.JOIN_FLOOR, Actions.JOIN_FLOOR_SUCCESS, {
          targetFloorId,
          currentFloorId,
          eventId,
        });
      }
    },
    onError: (
      error: Error,
      { targetTableId, targetFloorId, currentFloorId },
    ) => {
      const errorMessage = getErrorMessage(error.message);

      if (errorMessage) {
        dispatch(
          addErrorNotification({
            message: t(errorMessage),
          }),
        );
      }
      trackActionEnd(Actions.JOIN_ROOM, Actions.JOIN_ROOM_FAILURE, {
        targetTableId,
        eventId,
        floorId: targetFloorId,
        error: error.message,
      });
      if (currentFloorId !== targetFloorId) {
        trackActionEnd(Actions.JOIN_FLOOR, Actions.JOIN_FLOOR_FAILURE, {
          targetFloorId,
          eventId,
          reason: errorMessage,
        });
      }

      if (shouldRequestHostSeat(error.message) && userId) {
        addNotificationForHostSeat(targetTableId, userId);
      }
    },
    onSettled: () => {
      deleteEventUserNotification();
      dispatch(resetUserForceMove());
    },
  });

  const moveParticipant = useCallback(
    ({
      targetTableId,
      currentTableId,
      userId,
      targetFloorId,
      currentFloorId,
    }: Omit<UserForceMovePayload, "eventId">) => {
      if (isPending || !eventId || !targetFloorId || !targetTableId) {
        return;
      }

      mutate({
        currentFloorId,
        currentTableId,
        targetTableId,
        eventId,
        targetFloorId,
        userId,
      });
    },
    [eventId, isPending, mutate],
  );

  return { moveParticipant, isPending, status };
};
